import { useState } from "react";
import { Mail, ShieldCheck } from "lucide-react";
import { Button, ErrorBox, Field, Input } from "./kd";
import { errMsg } from "../lib/api";
import { toast } from "sonner";

type Props = {
  fixedEmailHint?: string;
  onRequest: (email: string) => Promise<any>;
  onVerify: (email: string, code: string) => Promise<any>;
  title?: string;
  intro?: string;
};

export function CodeGate({ fixedEmailHint, onRequest, onVerify, title = "E-Mail bestätigen", intro }: Props) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const request = async () => {
    setErr("");
    if (!fixedEmailHint && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setErr("Bitte geben Sie eine gültige E-Mail-Adresse an.");
    setBusy(true);
    try {
      await onRequest(email.trim().toLowerCase());
      setSent(true);
      toast.success("Der Code wurde versendet. Bitte prüfen Sie Ihr Postfach.");
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setErr("");
    if (!/^\d{6}$/.test(code.trim())) return setErr("Bitte geben Sie den 6-stelligen Code ein.");
    setBusy(true);
    try { await onVerify(email.trim().toLowerCase(), code.trim()); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="code-gate">
      <h2 className="mb-1 flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-primary" aria-hidden />{title}</h2>
      <p className="mb-5 text-text-muted">
        {intro || (fixedEmailHint ? `Wir senden einen 6-stelligen Code an die hinterlegte Adresse ${fixedEmailHint}.` : "Wir senden Ihnen einen 6-stelligen Code per E-Mail. Er ist 10 Minuten gültig.")}
      </p>
      <ErrorBox testid="code-gate-error">{err}</ErrorBox>
      {!fixedEmailHint && (
        <Field label="E-Mail-Adresse Ihrer Praxis" required htmlFor="cg-email">
          <Input id="cg-email" type="email" autoComplete="email" inputMode="email" value={email} disabled={sent} onChange={(e) => setEmail(e.target.value)} data-testid="code-email-input" />
        </Field>
      )}
      {!sent ? (
        <Button onClick={request} loading={busy} data-testid="code-request-button"><Mail className="h-4 w-4" aria-hidden />Code anfordern</Button>
      ) : (
        <form className="kd-fade" onSubmit={(e) => { e.preventDefault(); verify(); }}>
          <Field label="Bestätigungscode" required htmlFor="cg-code" hint="6 Ziffern aus der E-Mail">
            <Input id="cg-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="max-w-[200px] tracking-[0.3em]" data-testid="code-input" />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button type="submit" loading={busy} data-testid="code-verify-button">Bestätigen</Button>
            <Button type="button" variant="secondary" onClick={() => { setSent(false); setCode(""); }} data-testid="code-resend-button">Neuen Code anfordern</Button>
          </div>
        </form>
      )}
    </div>
  );
}
